/**
 * How a problem-invoice row reads and what it may offer.
 *
 * Two different facts get confused easily and must stay apart:
 *
 * - "a decision is already on file at the Tax Authority" (SHAAM answered 464 to a decision send).
 *   This is never a rejection: SHAAM only refuses to *change* a decision it already holds, and it
 *   does not tell us which one. It says nothing about whether the document was issued.
 *
 * - "this reference still owes its document" (status AwaitingDocument). Only the server can tell,
 *   because it alone knows whether a local document has since been issued on the number. The same
 *   464 sits in the history of a held reference and of a finished one.
 *
 * Deriving the second fact from the first made settled rows offer to finish a document that
 * already existed; the dialog behind that button re-read the status, found nothing pending, and
 * fell back to the decision menu.
 */

/** 464 = "Decision cannot be changed" — a decision exists, the send did not fail. */
export const textLooksLikeDecisionAlreadyOnFile = (text) => {
    if (!text) return false;
    const s = String(text);
    return (
        s.includes('"code":464') ||
        s.includes('"code": 464') ||
        s.toLowerCase().includes('decision cannot be changed')
    );
};

/** True when the Tax Authority holds a decision for this reference, issued document or not. */
export const rowHasDecisionAlreadyOnFile = (row) => {
    if (!row) return false;
    if (row.decisionAlreadyOnFile || row.DecisionAlreadyOnFile) return true;
    if (row.acceptedDecisionAction || row.AcceptedDecisionAction) return true;

    const history = row.decisionHistory || row.DecisionHistory || [];
    return (Array.isArray(history) ? history : []).some((d) => {
        if (!d) return false;
        if (typeof d === 'string') return textLooksLikeDecisionAlreadyOnFile(d);
        if (Number(d.apiStatus ?? d.ApiStatus ?? 0) === 464) return true;
        return (
            textLooksLikeDecisionAlreadyOnFile(d.errorDetails || d.ErrorDetails) ||
            textLooksLikeDecisionAlreadyOnFile(d.apiMessage || d.ApiMessage)
        );
    });
};

/** True only while the reference still has to be issued — the server's word, not a guess. */
export const rowAwaitsDocument = (row) =>
    !!row && (row.status || row.Status) === 'AwaitingDocument';
